import { useAuth0 } from "@auth0/auth0-react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { customClient } from "@utils/helpers";
import { useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { useDebounce } from "use-debounce";

import {
  GetReportsDocument,
  ReportType,
  ReportTypeEdge,
  ReportTypeEnums,
  useGetUserAccountDetailsQuery,
} from "@api/graphql/types-and-hooks";
import {
  AssessmentReport,
  AssessmentReportsTable,
  StatusType,
} from "@components/AssessmentReportsTable/AssessmentReportsTableV2";
import { CreateNewClientDialog } from "@components/CreateNewClientDialog/CreateNewClientDialog";
import { NewAssessmentDialog } from "@components/NewAssessmentDialog/NewAssessmentDialogV2";
import { WalkthroughDialog } from "@components/WalkthroughDialog/WalkthroughDialog";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import "@components/notifications/notifications.css";
import { useAdminData } from "@providers/AdminDataProvider";
import { getStatusAndRedirectRouteFromReport } from "@routes/AssessmentReportsV2/AssessmentReportsHelper";
import { CreditHeader } from "@routes/AssessmentReportsV2/CreditHeader";

const mapEdgeToReport = (edge: ReportTypeEdge): AssessmentReport | null => {
  const node = edge?.node;
  if (!node) return null;
  const clientFirstName = node?.reportClientDetails?.clientFirstName || "N/A";
  const clientLastName = node?.reportClientDetails?.clientLastName || "";
  const { status, redirectRoute } = getStatusAndRedirectRouteFromReport(node as ReportType);
  const createdByFirst = node?.createdBy?.provider?.providermetadata?.firstName || "";
  const createdByLast = node?.createdBy?.provider?.providermetadata?.lastName || "";
  const createdBy = node?.reportWriter || [createdByFirst, createdByLast].filter(Boolean).join(" ") || "—";
  return {
    id: node?.id,
    clientName: `${clientFirstName} ${clientLastName}`,
    reportTemplate: node?.reportTemplate ?? null,
    createdBy,
    learnerId: node?.learner?.id,
    dateCreated: new Date(node?.createdDate),
    lastUpdated: new Date(node?.lastUpdatedDate),
    status,
    redirectRoute,
  };
};

export const AssessmentReportsV2: React.FC = () => {
  const go = useNavigate();
  const { pathname } = useLocation();
  const { assessmentCreditsRemaining, isSelfServeUser, isCatalightUser } = useAdminData();

  const [tableData, setTableData] = useState<AssessmentReport[]>([]);
  const [isDialogOpen, setDialogOpen] = useState(false);

  const accountDetails = useGetUserAccountDetailsQuery(
    {},
    {
      queryKey: ["get-account-details"],
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnMount: "always",
      staleTime: Infinity,
      enabled: isSelfServeUser,
    },
  );

  const { refetch: accountRefetch } = accountDetails;

  const { getAccessTokenSilently } = useAuth0();
  const pageItemsDefault: number = 20;
  const [pageItems, setPageItems] = useState<number>(pageItemsDefault);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalItemCount, setTotalItemCount] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<StatusType[]>([]);

  // add debounce to delay querying until user stops typing
  const [debouncedSearchTerm] = useDebounce(searchTerm, 200);

  // refetch all data when searchTerm is cleared
  useEffect(() => {
    if (debouncedSearchTerm === "") {
      setTableData([]);
      refetch();
    }
  }, [debouncedSearchTerm]);

  const getAssessmentsQuery = useInfiniteQuery({
    queryKey: ["get-assessments", debouncedSearchTerm, pageItems],
    queryFn: async ({ pageParam = { after: null } }) => {
      const { after } = pageParam;
      const variables = {
        first: pageItems,
        after,
        records: 500,
        filter: {
          reportType: ReportTypeEnums.InitialAssessment,
          searchTerm: debouncedSearchTerm,
          sortBy: "-last_updated_date",
        },
      };
      const token = await getAccessTokenSilently();
      const response = await customClient({ token, document: GetReportsDocument, variables });

      return response.getReports;
    },
    initialPageParam: { after: null },
    getNextPageParam: (lastPage) => {
      if (lastPage.pageInfo.hasNextPage) {
        return {
          after: lastPage.pageInfo.endCursor, // Use the cursor for more efficient pagination
        };
      }
      return undefined; // No more pages to fetch
    },
    retry: false,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
    staleTime: Infinity,
    // staleTime: 0, // always refetch when searchTerm changes
    gcTime: 0, // immediately garbage collect old data
  });

  const { data, refetch, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage } = getAssessmentsQuery;

  // callback function used by the table when a page change occurs
  const getCurrentPageData = (currentPage: number, itemsPerPage: number) => {
    // check if the page number is higher than the current, fetch the next page; the table component will handle decreasing page #
    if (currentPage > pageNumber) {
      fetchNextPage();
      setPageNumber(currentPage);
      return;
    }
    setPageNumber(currentPage);
    setPageItems(itemsPerPage);
  };

  const previousAbPath = useRef<string>("");

  useEffect(() => {
    const abPath = pathname.split("/assessment-reports")?.pop() || "";
    if (abPath === "" && previousAbPath.current.length) {
      refetch();
      if (isSelfServeUser) {
        accountRefetch();
      }
    }
    previousAbPath.current = abPath;
  }, [pathname]);

  useEffect(() => {
    const pageData = data?.pages[pageNumber - 1];

    if (pageData) {
      setTotalItemCount(pageData?.totalCount);
      const mappedData = pageData.edges.map(mapEdgeToReport).filter(Boolean) as AssessmentReport[];
      setTableData(mappedData);
    }
  }, [data, pageNumber]);

  const isFiltering = statusFilter.length > 0;

  // The status filter is derived client-side from payorreportsubmissionSet
  // and review state, so the API can't filter it server-side today. While a
  // filter is active, eagerly pull every remaining server page so the visible
  // result count reflects the full dataset instead of only the current window.
  // Trade-off: for very large orgs this fans out N sequential GetReports
  // calls on each filter activation. Acceptable while typical org sizes stay
  // in the hundreds; the long-term fix is a backend-supported lifecycle-
  // status filter (tracked separately) so the client can stop fetch-all.
  useEffect(() => {
    if (isFiltering && hasNextPage && !isFetching && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [isFiltering, hasNextPage, isFetching, isFetchingNextPage, fetchNextPage]);

  // Flatten every loaded page into a single AssessmentReport[] so the filter
  // applies across the whole dataset, not only the current page's window.
  const allLoadedReports = useMemo<AssessmentReport[]>(() => {
    if (!isFiltering) return [];
    return data?.pages.flatMap((page) => page.edges.map(mapEdgeToReport).filter(Boolean) as AssessmentReport[]) ?? [];
  }, [isFiltering, data]);

  useEffect(() => {
    if (window.location.search.includes("redirect_status=succeeded")) {
      notifySuccess("Payment successful");
    } else if (window.location.search.includes("redirect_status=failed")) {
      notifyError("Payment failed");
    }
  }, []);

  return (
    <div className="mx-auto  w-full flex-col content-between items-center lg:flex-col lg:items-start">
      {pathname === "/assessment-reports" ? (
        <CreditHeader
          isSelfServeUser={isSelfServeUser}
          setDialogOpen={setDialogOpen}
          credits={assessmentCreditsRemaining}
          refetch={refetch}
        />
      ) : null}
      {<div className="pendo-banner" />}
      {pathname === "/assessment-reports" ? (
        <AssessmentReportsTable
          reports={isFiltering ? allLoadedReports.filter((row) => statusFilter.includes(row.status)) : tableData}
          refetch={getAssessmentsQuery.refetch}
          loading={isFetching || isFetchingNextPage}
          onRowClick={(report) => {
            go(`${report.id}/${report.redirectRoute}`, {
              state: { ...report },
            });
          }}
          onPageChange={getCurrentPageData}
          pageItems={pageItems}
          totalItemCount={totalItemCount}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
        />
      ) : (
        <Outlet
          context={{
            refetch,
          }}
        />
      )}
      {isCatalightUser ? (
        <CreateNewClientDialog isDialogOpen={isDialogOpen} setDialogOpen={(open) => setDialogOpen(open)} />
      ) : (
        <NewAssessmentDialog
          isSelfServeUser={isSelfServeUser}
          isDialogOpen={isDialogOpen}
          credits={assessmentCreditsRemaining}
          setDialogOpen={(open) => setDialogOpen(open)}
        />
      )}
      {/* <NewAssessmentDialog
        isSelfServeUser={isSelfServeUser}
        isDialogOpen={isDialogOpen}
        credits={assessmentCreditsRemaining}
        setDialogOpen={(open) => setDialogOpen(open)}
      /> */}

      <WalkthroughDialog />
    </div>
  );
};
